import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common/Bits";

export default function NewTicketDialog({ open, onOpenChange, channels }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ channel_id: "", subject: "", from_email: "", from_name: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const channel = channels.find((c) => c.id === form.channel_id);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/tickets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.number} created in ${data.channel_name}${data.assignee_name ? ` → ${data.assignee_name}` : ""}`);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      onOpenChange(false);
      setForm({ channel_id: "", subject: "", from_email: "", from_name: "", body: "" });
      setFiles([]);
      navigate(`/tickets/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-ticket-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Simulate an incoming email</DialogTitle>
            <DialogDescription>Creates a ticket exactly as if this email had arrived at the channel's address — including every attachment.</DialogDescription>
          </DialogHeader>
          <Field label="To (channel)">
            <Select value={form.channel_id} onValueChange={upd("channel_id")} required>
              <SelectTrigger data-testid="new-ticket-channel-select"><SelectValue placeholder="Pick a channel" /></SelectTrigger>
              <SelectContent className="bg-popover">{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} <span className="mono text-muted-foreground">· {c.inbound_email}</span></SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From email"><Input type="email" required value={form.from_email} onChange={upd("from_email")} placeholder="customer@example.com" className="mono text-xs" data-testid="new-ticket-from-input" /></Field>
            <Field label="From name"><Input value={form.from_name} onChange={upd("from_name")} placeholder="Jordan Reyes" data-testid="new-ticket-from-name-input" /></Field>
          </div>
          <Field label="Subject"><Input required value={form.subject} onChange={upd("subject")} placeholder="Quote request for…" data-testid="new-ticket-subject-input" /></Field>
          <Field label="Body"><Textarea rows={4} value={form.body} onChange={upd("body")} placeholder="Email body…" data-testid="new-ticket-body-input" /></Field>
          <Field label="Attachments" hint="Any file type: screenshots, PDFs, spreadsheets, zips — up to 25 MB each.">
            <Input type="file" multiple onChange={(e) => setFiles((f) => [...f, ...Array.from(e.target.files || [])])} className="text-xs file:mr-3 file:border-0 file:bg-muted file:px-2 file:text-xs" data-testid="new-ticket-files-input" />
          </Field>
          {files.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" data-testid="new-ticket-file-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-1 border border-border bg-muted/50 px-2 py-1 text-[11px]">
                  <Paperclip className="h-3 w-3" />{f.name} <span className="text-muted-foreground">{fmtBytes(f.size)}</span>
                  <button type="button" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <div className="mr-auto text-[11px] text-muted-foreground">{channel ? `Routing: ${channel.assignment_mode.replace("_", " ")} · ${channel.members.length} member(s)` : ""}</div>
            <Button type="submit" disabled={busy || !form.channel_id} data-testid="new-ticket-submit-btn">{busy ? "Creating…" : "Deliver email"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
